import { useEffect, useMemo, useState } from 'react';
import { X, AlertCircle, AlertTriangle, Loader2, CheckCircle, RefreshCw, Search, Info, ChevronDown, ChevronUp, Eye } from 'lucide-react';
import { authenticatedFetch } from '../lib/auth';
import { LmsUrlInstructions } from './LmsUrlInstructions';
import type { LmsAdapter, LmsCourseOption, MappingFormData, MappingRow } from '../lib/lms-adapters/types';
import { getLmsErrorInfo, inferLmsErrorCode } from '../utils/lmsErrorMessages';

// v6.7.0 — struktur-analyse (LMS-automatisk vurderingsform-deteksjon)
interface MappingPreview {
  eligible: boolean;
  not_supported?: boolean;
  reason?: string;
  suggested_slug?: string | null;
  confidence?: 'high' | 'medium' | 'low';
  summary?: string;
  counts?: Record<string, number>;
  activities?: Array<{ title: string; type: string; weight?: number }>;
  admin_override?: {
    allowed: boolean;
    message?: string;
    severity?: 'ok' | 'warning' | 'critical';
  };
}

// Oversett SKOS-slug til norsk prefLabel (match SKOS-vocab-ruta)
const SLUG_LABELS: Record<string, string> = {
  'external-sensor-lms': 'Ekstern sensor (LMS)',
  'internal-assessor-lms': 'Intern vurderer (LMS)',
  'controlled-exam-lms': 'Kontrollert eksamen (LMS)',
  'peer-review-lms': 'Likemannsvurdering (LMS)',
  'digital-test-lms': 'Digital test (LMS)',
  'controlled-exam': 'Kontrollert eksamen'
};

// Oversett assessment_type (dropdown) til LMS-slug for sammenligning med funn
const ASSESSMENT_TYPE_TO_SLUG: Record<string, string> = {
  'external_sensor': 'external-sensor-lms',
  'internal_assessor': 'internal-assessor-lms',
  'controlled_exam': 'controlled-exam-lms'
};

interface Props {
  /** LMS-adapter — bestemmer felter og endepunkter */
  adapter: LmsAdapter;
  /** Medlemsorganisasjonens ID */
  memberId: string;
  /** Hvis satt: rediger eksisterende mapping. Hvis null: ny */
  existing?: MappingRow | null;
  /** Lukk modal */
  onClose: () => void;
  /** Kalt etter vellykket lagring */
  onSaved: () => void;
}

interface StudyPlan {
  id: string;
  title: string;
  status: string;
  ects_credits?: number;
}

interface PlanModule {
  id: string;
  module_code: string;
  title: string;
  ects_credits: number | null;
  estimated_hours: number | null;
}

const ASSESSMENT_TYPES = [
  {
    value: 'external_sensor',
    label: 'Ekstern sensor',
    description: 'En sensor utenfor kursleverandøren har vurdert kandidatens læringsutbytter mot studieplanen. Sensor skal være faglig kvalifisert og uavhengig av undervisningen. Vurderingen dokumenteres og kan etterprøves.'
  },
  {
    value: 'internal_assessor',
    label: 'Intern vurderer',
    description: 'En kvalifisert vurderer hos kursleverandøren har vurdert kandidaten mot læringsutbyttene. Vurderingen skal være dokumenterbar.'
  },
  {
    value: 'controlled_exam',
    label: 'Kontrollert eksamen',
    description: 'Kandidaten har bestått eksamen under kontrollerte forhold — enten via vårt SEB-eksamenssystem (Safe Exam Browser med identitetskontroll og låst miljø) eller fysisk eksamen med tilsyn. Hjelpemidler er regulert i henhold til studieplanens vurderingskriterier.'
  }
];

export function LmsMappingModal({ adapter, memberId, existing, onClose, onSaved }: Props) {
  const isEditing = !!existing;

  const [studyPlans, setStudyPlans] = useState<StudyPlan[]>([]);
  const [modules, setModules] = useState<PlanModule[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Backend-code hentet fra response (separat fra fri-tekst error som kommer fra validering)
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<{ code?: string; message?: string; found_url?: string } | null>(null);

  const initialAssessmentType: MappingFormData['assessmentType'] =
    // Hvis eksisterende mapping har `unverified` (historisk verdi), tving den til external_sensor
    // — vi tillater ikke at noen lagrer som unverified lenger.
    existing?.assessment_type === 'external_sensor' ||
    existing?.assessment_type === 'internal_assessor' ||
    existing?.assessment_type === 'controlled_exam'
      ? existing.assessment_type
      : 'external_sensor';

  const [formData, setFormData] = useState<MappingFormData>({
    lmsCourseId: existing?.lms_course_id || '',
    lmsCourseName: existing?.lms_course_name || '',
    studyPlanId: existing?.study_plan_id || '',
    assessmentType: initialAssessmentType,
    moduleCodes: existing?.module_codes || [],
    // Ved redigering: anta at bekreftelsene allerede er gjort i opprinnelig opprettelse,
    // men krev at brukeren bekrefter på nytt for endring.
    noSelfAssessmentConfirmed: false,
    controlledConditionsConfirmed: false,
    ectsCredits: null,
    certificateTitle: null,
    certificateDescription: null
  });
  const [moduleCoverage, setModuleCoverage] = useState<'full' | 'subset'>(
    existing?.module_codes && existing.module_codes.length > 0 ? 'subset' : 'full'
  );

  // Kursliste fra LMS — hentes kun hvis adapter har endpoint OG vi ikke redigerer
  const supportsCourseList = !!adapter.endpoints.listCoursesByMember && !!adapter.normalizeCourseList;
  const [courseList, setCourseList] = useState<LmsCourseOption[]>([]);
  const [coursesLoading, setCoursesLoading] = useState(false);
  const [coursesError, setCoursesError] = useState<string | null>(null);
  const [courseSearch, setCourseSearch] = useState('');
  // Når true: vis manuell input (fallback for kurs som ikke finnes i listen)
  const [manualMode, setManualMode] = useState(false);

  // v6.7.0 — mapping-preview (struktur-analyse)
  const [preview, setPreview] = useState<MappingPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [showPreviewDetails, setShowPreviewDetails] = useState(false);

  const loadCourses = async () => {
    if (!supportsCourseList || !adapter.endpoints.listCoursesByMember || !adapter.normalizeCourseList) return;
    setCoursesLoading(true);
    setCoursesError(null);
    try {
      const res = await authenticatedFetch(adapter.endpoints.listCoursesByMember(memberId));
      const data = await res.json();
      if (!res.ok) {
        if (res.status === 404) {
          setCoursesError('Ingen aktiv integrasjon mot denne LMS-en for medlemsorganisasjonen.');
        } else {
          const fullMessage = data.guidance
            ? `${data.error || 'Feil fra LMS'}\n\nLøsning: ${data.guidance}`
            : (data.error || 'Kunne ikke hente kurs fra LMS');
          setCoursesError(fullMessage);
        }
        setCourseList([]);
        return;
      }
      const courses = Array.isArray(data) ? data : (data.courses || []);
      setCourseList(adapter.normalizeCourseList(courses));
    } catch (err) {
      setCoursesError(err instanceof Error ? err.message : 'Kunne ikke hente kurs');
      setCourseList([]);
    } finally {
      setCoursesLoading(false);
    }
  };

  useEffect(() => {
    if (supportsCourseList && !isEditing) {
      loadCourses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberId, adapter.lmsType]);

  const filteredCourses = useMemo(() => {
    if (!courseSearch.trim()) return courseList;
    const q = courseSearch.toLowerCase();
    return courseList.filter(c =>
      c.name.toLowerCase().includes(q) ||
      c.id.toLowerCase().includes(q) ||
      (c.secondary || '').toLowerCase().includes(q)
    );
  }, [courseList, courseSearch]);

  // Last godkjente studieplaner (samme endepunkt som MoodleSettings bruker)
  useEffect(() => {
    (async () => {
      try {
        const res = await authenticatedFetch('/api/study-plans/versions/approved', {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        });
        if (!res.ok) {
          console.error('study-plans/versions/approved failed:', res.status);
          return;
        }
        const data = await res.json();
        if (Array.isArray(data.study_plans)) {
          setStudyPlans(data.study_plans);
        } else {
          console.error('Unexpected response:', data);
        }
      } catch (err) {
        console.error('Failed to load study plans', err);
      }
    })();
  }, []);

  // Last moduler når studieplan velges
  useEffect(() => {
    if (!formData.studyPlanId) {
      setModules([]);
      return;
    }
    (async () => {
      try {
        const res = await authenticatedFetch(`/api/ai/modules/${formData.studyPlanId}`);
        const data = await res.json();
        if (data.success && Array.isArray(data.modules)) {
          setModules(data.modules);
        } else {
          setModules([]);
        }
      } catch (err) {
        console.error('Failed to load modules', err);
        setModules([]);
      }
    })();
  }, [formData.studyPlanId]);

  // v6.7.0 — kall preview-API når bruker har valgt kurs
  const runPreview = async () => {
    if (!formData.lmsCourseId) {
      setPreviewError('Velg kurs først');
      return;
    }
    setPreviewLoading(true);
    setPreviewError(null);
    setPreview(null);
    try {
      const adminSlug = ASSESSMENT_TYPE_TO_SLUG[formData.assessmentType] || formData.assessmentType;
      const res = await authenticatedFetch(`/api/mapping-preview/${adapter.lmsType}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          member_id: memberId,
          lms_course_id: formData.lmsCourseId,
          admin_slug: adminSlug
        })
      });
      const data = await res.json();
      if (!res.ok) {
        setPreviewError(data.error || data.message || 'Kunne ikke hente struktur-analyse');
        return;
      }
      setPreview(data);
    } catch (e) {
      setPreviewError(e instanceof Error ? e.message : 'Kunne ikke hente struktur-analyse');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSubmit = async () => {
    setError(null);
    setErrorCode(null);
    setValidationError(null);

    // v6.7.0 — hvis preview er kjørt og sier !eligible, blokker lagring
    if (preview && !preview.not_supported && preview.eligible === false) {
      setError(
        'Dette kurset har ingen vurderingsinnhold som kan brukes til kompetanseattest. ' +
        'Vurderingen må være reell (ekstern sensor, intern vurderer eller kontrollert eksamen) — ' +
        'ikke bare lesing/visning. Legg til en oppgave (assignment/essay/workshop) eller kontrollert quiz i kurset og prøv igjen.'
      );
      return;
    }

    // v6.7.0 — hvis admin override konflikterer med funn, krev endring
    if (preview && preview.admin_override && !preview.admin_override.allowed) {
      setError(
        preview.admin_override.message ||
        'Valgt vurderingstype passer ikke med det LMS-et viser. Juster vurderingstype eller endre kursets vurderingsmetode.'
      );
      return;
    }

    // Klient-side validering
    const courseIdError = adapter.courseIdField.validate(formData.lmsCourseId);
    if (courseIdError) { setError(courseIdError); return; }
    if (!formData.studyPlanId) { setError('Velg en studieplan'); return; }
    if (adapter.courseNameField?.required && !formData.lmsCourseName.trim()) {
      setError('Kursnavn er påkrevd');
      return;
    }

    // Hvis subset valgt men ingen moduler huket av
    if (moduleCoverage === 'subset' && formData.moduleCodes.length === 0) {
      setError('Velg minst én modul, eller bytt til "Hele studieplanen"');
      return;
    }

    // Bekreftelses-flagg kreves av backend for Moodle/TutorLMS/LearnDash.
    // Xtramile bruker dem ikke, men det er trygt å sende dem uansett.
    if (!formData.noSelfAssessmentConfirmed) {
      setError('Du må bekrefte at kandidatene ikke foretar egenvurdering');
      return;
    }
    if (formData.assessmentType === 'controlled_exam' && !formData.controlledConditionsConfirmed) {
      setError('Du må bekrefte at eksamen gjennomføres under kontrollerte forhold');
      return;
    }

    setSubmitting(true);
    try {
      let url: string;
      let method: 'POST' | 'PATCH';
      let body: Record<string, unknown>;

      if (isEditing && existing) {
        url = adapter.endpoints.updateMapping(existing.mapping_id);
        method = 'PATCH';
        body = adapter.buildUpdateBody(formData);
      } else {
        url = adapter.endpoints.createMappingForMember(memberId);
        method = 'POST';
        body = adapter.buildCreateBody(formData);
      }

      const res = await authenticatedFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await res.json();

      if (!res.ok) {
        // URL-validering-feilkoder (Task #14) — egen bobble m/instruksjoner
        if (data.code && ['no_description', 'no_studyplan_url', 'studyplan_mismatch',
            'studyplan_not_active', 'module_mismatch', 'unknown_approval_link'].includes(data.code)) {
          setValidationError({ code: data.code, message: data.message, found_url: data.found_url });
        } else {
          // Alle andre feil — bruk felles error-mapping i utils/lmsErrorMessages.ts
          const rawMessage = data.message || data.error || 'Lagring feilet';
          const code = data.code || inferLmsErrorCode(rawMessage);
          setError(rawMessage);
          setErrorCode(code);
        }
        return;
      }

      onSaved();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Lagring feilet');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className={`${adapter.colors.bg} ${adapter.colors.border} border-b px-6 py-4 flex items-center justify-between rounded-t-lg`}>
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {isEditing ? 'Rediger kurskobling' : 'Ny kurskobling'} — {adapter.displayName}
            </h2>
            <p className="text-sm text-gray-700 mt-1">{adapter.shortDescription}</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-900">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-5">
          {/* Studieplan */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Godkjent studieplan
            </label>
            <select
              value={formData.studyPlanId}
              onChange={(e) => setFormData({ ...formData, studyPlanId: e.target.value, moduleCodes: [] })}
              disabled={isEditing}
              className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-kf-blue-500"
            >
              <option value="">— Velg studieplan —</option>
              {studyPlans.map(sp => (
                <option key={sp.id} value={sp.id}>
                  {sp.title}{sp.ects_credits ? ` (${sp.ects_credits} sp)` : ''}
                </option>
              ))}
            </select>
            {isEditing && (
              <p className="text-xs text-gray-500 mt-1">Studieplan kan ikke endres på eksisterende kobling. Slett og opprett ny.</p>
            )}
          </div>

          {/* Modul-utvalg (Task #192) */}
          {formData.studyPlanId && modules.length > 0 && (
            <div className="border-t pt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Hva dekker kurset?
              </label>
              <div className="flex gap-4 mb-2">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="radio"
                    name="moduleCoverage"
                    checked={moduleCoverage === 'full'}
                    onChange={() => { setModuleCoverage('full'); setFormData({ ...formData, moduleCodes: [] }); }}
                  />
                  <span>Hele studieplanen ({modules.length} moduler)</span>
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="radio"
                    name="moduleCoverage"
                    checked={moduleCoverage === 'subset'}
                    onChange={() => setModuleCoverage('subset')}
                  />
                  <span>Et utvalg moduler</span>
                </label>
              </div>

              {moduleCoverage === 'subset' && (
                <div className="bg-gray-50 border rounded p-3 space-y-1.5">
                  <p className="text-xs text-gray-600 mb-2">
                    Kompetanseattesten reflekterer kun læringsutbyttene i de valgte modulene.
                  </p>
                  {modules.map((mod) => {
                    const checked = formData.moduleCodes.includes(mod.module_code);
                    return (
                      <label key={mod.id} className="flex items-start gap-2 text-sm cursor-pointer hover:bg-white p-1.5 rounded">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            const newCodes = e.target.checked
                              ? [...formData.moduleCodes, mod.module_code]
                              : formData.moduleCodes.filter(c => c !== mod.module_code);
                            setFormData({ ...formData, moduleCodes: newCodes });
                          }}
                          className="mt-0.5"
                        />
                        <div className="flex-1">
                          <span className="font-medium">{mod.module_code}: {mod.title}</span>
                          <span className="text-xs text-gray-500 ml-2">
                            {mod.ects_credits ? `${mod.ects_credits} sp` : ''}
                          </span>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* URL-instruksjoner (Task #14) */}
          <div className="border-t pt-4">
            <LmsUrlInstructions
              studyPlanId={formData.studyPlanId || null}
              validationError={validationError}
            />
          </div>

          {/* Kurs fra LMS — picker hvis støttet, ellers manuell input */}
          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Velg kurs i {adapter.displayName}
              </label>
              {supportsCourseList && !isEditing && (
                <div className="flex items-center gap-3 text-xs">
                  <button
                    type="button"
                    onClick={loadCourses}
                    disabled={coursesLoading}
                    className="text-gray-600 hover:text-gray-900 inline-flex items-center gap-1"
                    title="Hent kursliste på nytt fra LMS"
                  >
                    <RefreshCw className={`h-3 w-3 ${coursesLoading ? 'animate-spin' : ''}`} />
                    Oppdater liste
                  </button>
                  <button
                    type="button"
                    onClick={() => setManualMode(m => !m)}
                    className="text-kf-blue-600 hover:underline"
                  >
                    {manualMode ? 'Velg fra liste' : 'Skriv inn manuelt'}
                  </button>
                </div>
              )}
            </div>

            {/* Picker — kun når støttet, ikke i edit-mode, ikke i manualMode */}
            {supportsCourseList && !isEditing && !manualMode ? (
              <div>
                {coursesLoading && (
                  <div className="flex items-center gap-2 text-sm text-gray-600 p-3 bg-gray-50 border rounded">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Henter kurs fra {adapter.displayName}…
                  </div>
                )}

                {!coursesLoading && coursesError && (
                  <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-900 space-y-2">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                      <span className="whitespace-pre-line">{coursesError}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setManualMode(true)}
                      className="text-xs text-kf-blue-600 hover:underline"
                    >
                      → Skriv inn kurs-ID manuelt i stedet
                    </button>
                  </div>
                )}

                {!coursesLoading && !coursesError && (
                  <div className="space-y-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Søk i kursnavn, ID eller slug…"
                        value={courseSearch}
                        onChange={(e) => setCourseSearch(e.target.value)}
                        className="w-full pl-9 pr-3 py-2 border rounded focus:ring-2 focus:ring-kf-blue-500 text-sm"
                      />
                    </div>

                    {courseList.length === 0 ? (
                      <p className="text-sm text-gray-500 italic p-3">
                        Ingen kurs funnet i {adapter.displayName}. Sjekk at integrasjonen er aktiv.
                      </p>
                    ) : filteredCourses.length === 0 ? (
                      <p className="text-sm text-gray-500 italic p-3">
                        Ingen kurs matcher "{courseSearch}".
                      </p>
                    ) : (
                      <div className="border rounded max-h-64 overflow-y-auto divide-y">
                        {filteredCourses.map(c => {
                          const selected = formData.lmsCourseId === c.id;
                          return (
                            <button
                              key={c.id}
                              type="button"
                              onClick={() => setFormData({
                                ...formData,
                                lmsCourseId: c.id,
                                lmsCourseName: c.name
                              })}
                              className={`w-full text-left px-3 py-2 hover:bg-gray-50 ${selected ? `${adapter.colors.bg} font-medium` : ''}`}
                            >
                              <div className="flex items-start gap-2">
                                {selected && <CheckCircle className={`h-4 w-4 mt-0.5 flex-shrink-0 ${adapter.colors.primary.split(' ')[0].replace('bg-', 'text-')}`} />}
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm text-gray-900 truncate">{c.name}</div>
                                  <div className="text-xs text-gray-500 truncate">
                                    ID: {c.id}{c.secondary ? ` · ${c.secondary}` : ''}
                                  </div>
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                    <p className="text-xs text-gray-500">
                      {courseList.length} kurs hentet fra {adapter.displayName}. Velg det kurset koblingen skal gjelde.
                    </p>
                  </div>
                )}
              </div>
            ) : (
              /* Manuell ID-input (Xtramile, edit-mode, eller manualMode-fallback) */
              <div className="space-y-3">
                <div>
                  <input
                    type={adapter.courseIdField.inputType}
                    value={formData.lmsCourseId}
                    onChange={(e) => setFormData({ ...formData, lmsCourseId: e.target.value })}
                    placeholder={adapter.courseIdField.placeholder}
                    disabled={isEditing}
                    className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-kf-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {adapter.courseIdField.label} — {adapter.courseIdField.helpText}
                  </p>
                </div>
                {adapter.courseNameField && (
                  <div>
                    <input
                      type="text"
                      value={formData.lmsCourseName}
                      onChange={(e) => setFormData({ ...formData, lmsCourseName: e.target.value })}
                      placeholder={adapter.courseNameField.placeholder}
                      className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-kf-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">{adapter.courseNameField.label}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Vurderingstype */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Vurderingstype (vises på attesten)
            </label>
            <select
              value={formData.assessmentType}
              onChange={(e) => setFormData({
                ...formData,
                assessmentType: e.target.value as MappingFormData['assessmentType'],
                // Hvis bruker bytter bort fra controlled_exam, nullstill bekreftelsen
                controlledConditionsConfirmed: e.target.value === 'controlled_exam'
                  ? formData.controlledConditionsConfirmed
                  : false
              })}
              className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-kf-blue-500"
            >
              {ASSESSMENT_TYPES.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
            <p className="text-xs text-gray-600 mt-1">
              {ASSESSMENT_TYPES.find(t => t.value === formData.assessmentType)?.description}
            </p>
            <p className="text-xs text-gray-500 mt-2 italic">
              Merk: Kompetanseattest krever reell vurdering av læringsutbyttene. Kurs uten vurdering
              kan ikke utstede attest gjennom dette systemet.
            </p>
          </div>

          {/* v6.7.0 — Struktur-analyse fra LMS */}
          {['moodle', 'tutorlms', 'learndash'].includes(adapter.lmsType) && (
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Struktur-analyse fra LMS
                </label>
                <button
                  type="button"
                  onClick={runPreview}
                  disabled={!formData.lmsCourseId || previewLoading}
                  className="text-xs px-3 py-1.5 rounded border border-kf-blue-300 bg-kf-blue-50 text-kf-blue-700 hover:bg-kf-blue-100 disabled:opacity-50 inline-flex items-center gap-1"
                >
                  {previewLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Eye className="h-3 w-3" />}
                  {preview ? 'Oppdater analyse' : 'Analyser kursinnhold'}
                </button>
              </div>

              {previewError && (
                <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-800 flex items-start gap-2">
                  <AlertCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                  <div>{previewError}</div>
                </div>
              )}

              {preview && preview.not_supported && (
                <div className="bg-gray-50 border border-gray-200 rounded p-3 text-xs text-gray-700 space-y-1">
                  <p className="font-medium text-gray-800">Automatisk analyse ikke tilgjengelig</p>
                  <p>{preview.reason || 'LMS-en støtter ikke henting av struktur. Bekreftelse av vurderingstype er din erklæring under kvalitetssikringsavtalen.'}</p>
                </div>
              )}

              {preview && !preview.not_supported && (
                <div className={`border rounded p-3 space-y-2 ${
                  !preview.eligible
                    ? 'bg-red-50 border-red-200'
                    : preview.admin_override && !preview.admin_override.allowed
                      ? 'bg-amber-50 border-amber-200'
                      : 'bg-green-50 border-green-200'
                }`}>
                  <div className="flex items-start gap-2 text-sm">
                    {preview.eligible && (!preview.admin_override || preview.admin_override.allowed) ? (
                      <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0 text-green-600" />
                    ) : (
                      <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0 text-red-600" />
                    )}
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">
                        {preview.summary || 'Analyse gjennomført'}
                      </p>
                      {preview.suggested_slug && (
                        <p className="text-xs text-gray-700 mt-1">
                          LMS-en indikerer: <strong>{SLUG_LABELS[preview.suggested_slug] || preview.suggested_slug}</strong>
                          {preview.confidence && <span className="text-gray-500"> · {
                            preview.confidence === 'high' ? 'høy sikkerhet' :
                            preview.confidence === 'medium' ? 'moderat sikkerhet' : 'lav sikkerhet'
                          }</span>}
                        </p>
                      )}
                      {!preview.eligible && preview.reason && (
                        <p className="text-xs text-red-800 mt-1">{preview.reason}</p>
                      )}
                      {preview.admin_override && !preview.admin_override.allowed && (
                        <p className="text-xs text-amber-800 mt-1">
                          <strong>Konflikt:</strong> {preview.admin_override.message}
                        </p>
                      )}
                    </div>
                  </div>

                  {(preview.activities?.length ?? 0) > 0 && (
                    <button
                      type="button"
                      onClick={() => setShowPreviewDetails(!showPreviewDetails)}
                      className="text-xs text-kf-blue-700 hover:text-kf-blue-900 inline-flex items-center gap-1"
                    >
                      {showPreviewDetails ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                      {showPreviewDetails ? 'Skjul detaljer' : `Vis detaljer (${preview.activities!.length} aktiviteter)`}
                    </button>
                  )}

                  {showPreviewDetails && preview.activities && (
                    <div className="bg-white border border-gray-200 rounded p-2 max-h-48 overflow-y-auto">
                      <table className="w-full text-xs">
                        <thead className="text-gray-500 border-b">
                          <tr>
                            <th className="text-left py-1">Aktivitet</th>
                            <th className="text-left py-1">Type</th>
                          </tr>
                        </thead>
                        <tbody>
                          {preview.activities.map((a, i) => (
                            <tr key={i} className="border-b border-gray-100 last:border-0">
                              <td className="py-1 pr-2 text-gray-800">{a.title}</td>
                              <td className="py-1 text-gray-600 font-mono text-[11px]">{a.type}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {!preview && !previewError && !previewLoading && (
                <p className="text-xs text-gray-500 italic">
                  Klikk "Analyser kursinnhold" for å se hva LMS-en inneholder av vurderingsaktiviteter.
                  Dette hjelper deg verifisere at valgt vurderingstype stemmer med kurset.
                </p>
              )}
            </div>
          )}

          {/* Bekreftelses-sjekkbokser (kvalitetsforpliktelse) */}
          <div className="border-t pt-4 space-y-3">
            <label className="block text-sm font-medium text-gray-700">
              Kvalitetsbekreftelse
            </label>

            {/* Alltid kreves: ingen egenvurdering */}
            <label className="flex items-start gap-2 text-sm cursor-pointer p-3 border border-gray-200 rounded hover:bg-gray-50">
              <input
                type="checkbox"
                checked={formData.noSelfAssessmentConfirmed}
                onChange={(e) => setFormData({ ...formData, noSelfAssessmentConfirmed: e.target.checked })}
                className="mt-0.5 flex-shrink-0"
              />
              <span className="text-gray-700">
                Jeg bekrefter at kandidatene <strong>ikke</strong> foretar egenvurdering — vurdering
                gjøres av sensor, vurderer eller via kontrollert eksamen.
              </span>
            </label>

            {/* Kun ved controlled_exam */}
            {formData.assessmentType === 'controlled_exam' && (
              <label className="flex items-start gap-2 text-sm cursor-pointer p-3 border border-amber-200 bg-amber-50 rounded hover:bg-amber-100">
                <input
                  type="checkbox"
                  checked={formData.controlledConditionsConfirmed}
                  onChange={(e) => setFormData({ ...formData, controlledConditionsConfirmed: e.target.checked })}
                  className="mt-0.5 flex-shrink-0"
                />
                <span className="text-gray-800">
                  Jeg bekrefter at eksamen gjennomføres under <strong>kontrollerte forhold</strong>
                  {' '}— enten gjennom vårt SEB-eksamenssystem (Safe Exam Browser) eller fysisk
                  eksamen med tilsyn. Kandidaten må identifiseres og kan ikke konsultere
                  uautoriserte hjelpemidler.
                </span>
              </label>
            )}
          </div>

          {/* Errors — strukturert visning med veiledning */}
          {error && (() => {
            const info = getLmsErrorInfo(errorCode, error);
            // Hvis vi ikke har en kjent kode, vis kun rå melding (samme som før)
            if (info.code === 'unknown') {
              return (
                <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-800 flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <div className="whitespace-pre-line">{error}</div>
                </div>
              );
            }
            // Kjent kode — vis strukturert med tittel, beskrivelse, veiledning
            return (
              <div className="bg-red-50 border border-red-200 rounded p-4 space-y-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-5 w-5 mt-0.5 flex-shrink-0 text-red-600" />
                  <div>
                    <p className="font-semibold text-red-900">{info.title}</p>
                    <p className="text-sm text-red-800 mt-1">{info.description}</p>
                  </div>
                </div>
                <div className="ml-7 bg-white border border-red-200 rounded p-3">
                  <p className="text-xs font-medium text-gray-700 mb-1 flex items-center gap-1">
                    <Info size={12} />
                    Hva du kan gjøre:
                  </p>
                  <p className="text-sm text-gray-800 whitespace-pre-line">{info.guidance}</p>
                  <p className="text-[11px] text-gray-500 mt-2 font-mono">Feilkode: {info.code}</p>
                </div>
              </div>
            );
          })()}
        </div>

        {/* Footer */}
        <div className="border-t px-6 py-4 flex justify-end gap-3 bg-gray-50 rounded-b-lg">
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded"
          >
            Avbryt
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className={`px-4 py-2 text-sm text-white rounded ${adapter.colors.primary} disabled:opacity-50 inline-flex items-center gap-2`}
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
            {isEditing ? 'Lagre endringer' : 'Opprett kobling'}
          </button>
        </div>
      </div>
    </div>
  );
}
